import { useCallback, useEffect, useMemo, useState } from "react";
import { NavMenuContextValue } from "./nav-menu-context";
import {
  findActiveNavMenuPath,
  findAllDescendentIds,
  getNodeById,
  makeNavMenuItemTree,
  NavMenuItemNode,
  safelyGetLastArrayItem,
} from "./nav-menu-utils";

const inferDefaultExpandedNavMenuItems = (
  menuItemTree: NavMenuItemNode,
  collapsible: unknown,
  activeNavMenuItem: string,
  activeNavMenuPath: string[]
) => {
  if (Boolean(collapsible) && activeNavMenuItem) {
    const activeNavMenuItemHasChildren = findAllDescendentIds(menuItemTree, activeNavMenuItem).length > 0;
    if (activeNavMenuItemHasChildren) {
      return activeNavMenuPath.slice(0, activeNavMenuPath.indexOf(activeNavMenuItem) + 1);
    } else {
      return activeNavMenuPath.slice(0, activeNavMenuPath.indexOf(activeNavMenuItem));
    }
  } else {
    return [];
  }
};

/**
 * This custom hook encapsulates reusable logic required to render Nav Menus.
 *
 * Given a React children sub-tree representing arbitrarily nested Nav Menu Items, this hook provides a way to track
 * the active menu item.
 *
 * It maintains the entire path leading up to the active nav menu item starting from the parent nav menu. This can be
 * useful if the intermediate nodes need to be styles differently.
 *
 * It provides a function to update the active nav menu item.
 *
 * It also supports the idea of an optional, default selected nav menu item. If present, it is used to initialize the
 * active nave menu path.
 *
 * @param id `id` of the Nav Menu.
 * @param label `label` of the Nav Menu.
 * @param children Nav menu items.
 * @param props Initial value for state that represents the nav menu props.
 * @param isMobile Simply set in to the context to be accessible by deeply nested child components.
 * @param mobileMenuCloseDelay Delay in milliseconds to wait before updating state that represents if the mobile menu is closed. (E.g. to allow for closing animation to play out)
 * @param onBeforeMobileMenuClose An event that will be triggered just before setting state to mark mobile menu as closed, done via the `closeMobileMenu` callback. (E.g. can be used to handle scroll-to-top-on-close)
 * @returns
 */
export const useNavMenu = <P extends Record<string, unknown>>(
  id: string,
  label: string,
  children: React.ReactChild[] | React.ReactChild,
  props: P,
  isMobile: boolean = false,
  mobileMenuCloseDelay: number = 0,
  onBeforeMobileMenuClose?: () => void
): NavMenuContextValue<P> => {
  const [navMenuProps, setNavMenuProps] = useState(props);

  /**
   * @todo - replace with deepEquals based deps array check
   */
  useEffect(() => {
    setNavMenuProps(props);
  }, [props]);

  const navMenuItemTree = useMemo(() => makeNavMenuItemTree(id, label, children)[0], [children, id, label]);

  const getMenuItemNode = useCallback((id: string) => getNodeById(navMenuItemTree, id), [navMenuItemTree]);

  const [activeNavMenuPath, setActiveNavMenuPath] = useState(findActiveNavMenuPath(navMenuItemTree));

  useEffect(() => {
    setActiveNavMenuPath(findActiveNavMenuPath(navMenuItemTree));
  }, [navMenuItemTree]);

  const [activeNavMenuItem, setActiveNavMenuItem] = useState(safelyGetLastArrayItem(activeNavMenuPath));

  useEffect(() => {
    setActiveNavMenuItem(safelyGetLastArrayItem(activeNavMenuPath));
  }, [activeNavMenuPath]);

  const getActiveMenuItemNode = useCallback(
    () => getNodeById(navMenuItemTree, activeNavMenuItem),
    [activeNavMenuItem, navMenuItemTree]
  );

  const updateActiveNavMenuPath = useCallback(
    (id: string) => {
      const activeNavMenuPath = findActiveNavMenuPath(navMenuItemTree, id);
      setActiveNavMenuPath(activeNavMenuPath);
      setActiveNavMenuItem(safelyGetLastArrayItem(activeNavMenuPath));
    },
    [navMenuItemTree]
  );

  const [expandedNavMenuItems, setExpandedNavMenuItems] = useState<string[]>(
    inferDefaultExpandedNavMenuItems(navMenuItemTree, props.collapsible, activeNavMenuItem, activeNavMenuPath)
  );

  useEffect(() => {
    setExpandedNavMenuItems(
      inferDefaultExpandedNavMenuItems(navMenuItemTree, props.collapsible, activeNavMenuItem, activeNavMenuPath)
    );
  }, [activeNavMenuItem, activeNavMenuPath, navMenuItemTree, props.collapsible]);

  const toggleNavMenuItemExpanded = useCallback(
    (id: string) => {
      setExpandedNavMenuItems((expandedNavMenuItems) => {
        if (expandedNavMenuItems.includes(id)) {
          const idsToCollapse = [id, ...findAllDescendentIds(navMenuItemTree, id)];
          return expandedNavMenuItems.filter((navMenuItemId) => !idsToCollapse.includes(navMenuItemId));
        } else {
          return [...expandedNavMenuItems, id];
        }
      });
    },
    [navMenuItemTree]
  );

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);

  const openMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(true);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuVisible(false);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuVisible(true);
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuVisible) {
      const timeoutId = setTimeout(() => {
        onBeforeMobileMenuClose?.();
        setIsMobileMenuOpen(false);
      }, mobileMenuCloseDelay);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isMobileMenuVisible, mobileMenuCloseDelay, onBeforeMobileMenuClose]);

  return {
    navMenuProps,
    getMenuItemNode,
    activeNavMenuItem,
    activeNavMenuPath,
    updateActiveNavMenuPath,
    getActiveMenuItemNode,
    expandedNavMenuItems,
    toggleNavMenuItemExpanded,
    isMobile,
    isMobileMenuOpen,
    isMobileMenuVisible,
    openMobileMenu,
    closeMobileMenu,
  };
};
